import React, { useState, useEffect, useRef } from 'react';
import { db, functions } from '../firebase/firebase-config';
import { doc, getDoc, collection, collectionGroup, query, where, getDocs, limit, writeBatch, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Camera, CheckCircle, AlertCircle, Printer, Scissors, Search, FileText, AlertTriangle, X } from 'lucide-react';
import { isTwoSided, printedBack as printedBackOf, printedDone, productionResetFields } from '../lib/production';
import { previewThumb, previewCard } from '../lib/previews';
import ReportFailureModal from '../components/JobView/Tabs/Modals/ReportFailureModal';
import { buildTaskAssignment, buildAssignmentEmail, buildAssignedByStamp } from '../utils/taskAssignment';
import { parseScanInput } from '../lib/scanCode';
import { findPieceAcrossSheets, pieceProductionStatus } from '../lib/pieces.mjs';

const ScanningStationView = ({ user, users = [] }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [pieceResults, setPieceResults] = useState([]);   // B1b: piezas halladas por código (texto)
    const [selectedSheet, setSelectedSheet] = useState(null);
    const [selectedPiece, setSelectedPiece] = useState(null); // B1b: { piece, jobId, jobName }
    const [jobData, setJobData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);
    
    // Abre una plancha directo por IDs (QR escaneado/pegado en el input — sin búsqueda).
    const openSheetByIds = async (jobId, sheetDocId) => {
        const sheetSnap = await getDoc(doc(db, 'jobs', jobId, 'sheets', sheetDocId));
        if (!sheetSnap.exists()) {
            setError(`La plancha del código escaneado ya no existe (job ${jobId}).`);
            return;
        }
        const jobSnap = await getDoc(doc(db, 'jobs', jobId));
        if (!jobSnap.exists()) {
            setError('No se pudo cargar la información del trabajo asociado.');
            return;
        }
        setJobData({ id: jobSnap.id, ...jobSnap.data() });
        setSelectedSheet({ ...sheetSnap.data(), id: sheetSnap.id, jobId });
        setSelectedPiece(null);
        setSearchResults([]);
        setPieceResults([]);
        setSearchTerm('');
    };

    // B1b: resuelve una PIEZA por código dentro de un job. Carga las planchas del job
    // y AGREGA (lógica pura compartida src/lib/pieces.mjs) — el QR de pieza trae el
    // jobId, así que esto funciona con TODOS los datos ya existentes (gate-free).
    const openPieceByCode = async (jobId, code) => {
        const jobSnap = await getDoc(doc(db, 'jobs', jobId));
        if (!jobSnap.exists()) { setError(`No se pudo cargar el trabajo ${jobId} de la pieza escaneada.`); return; }
        const sheetsSnap = await getDocs(collection(db, 'jobs', jobId, 'sheets'));
        const sheets = sheetsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        const piece = findPieceAcrossSheets(sheets, code);
        if (!piece) { setError(`No se encontró la pieza "${code}" en el trabajo ${jobId}.`); return; }
        setSelectedPiece({ piece, jobId, jobName: jobSnap.data().jobName || jobId });
        setSelectedSheet(null);
        setJobData(null);
        setSearchResults([]);
        setPieceResults([]);
        setSearchTerm('');
    };

    // Búsqueda §9.3 Pilar 3: input → parseScanInput → si trae IDs, lookup directo al
    // doc; si es texto, UNA collection-group query por `hriSearch` (case-insensitive,
    // sin '#'). Mata el N+1 de raíz (antes: 50 jobs × N queries). Requiere el índice
    // de collection-group y la regla de lectura de sheets (los despliega Daniel).
    const handleSearch = async (e) => {
        e.preventDefault();
        const parsed = parseScanInput(searchTerm);
        if (!parsed) return;

        setIsLoading(true);
        setError('');
        setSuccess('');
        setSearchResults([]);
        setPieceResults([]);
        setSelectedSheet(null);
        setSelectedPiece(null);

        try {
            if (parsed.kind === 'sheet' && parsed.jobId && parsed.id) {
                await openSheetByIds(parsed.jobId, parsed.id);
                return;
            }
            if (parsed.kind === 'piece' && parsed.jobId && parsed.id) {
                // B1b: el QR de pieza trae el jobId → resolución directa (gate-free).
                await openPieceByCode(parsed.jobId, parsed.id);
                return;
            }
            if (parsed.kind === 'artifact') {
                setError('Eso es una etiqueta de ARCHIVO. Úsala en el Buscador de Archivos.');
                return;
            }
            if (parsed.kind === 'unknown') {
                setError('Código no reconocido. Escanea el QR de una plancha/pieza o teclea su código (ej. 1116-COR_002P).');
                return;
            }

            // Texto: hri tecleado o escaneado de etiqueta vieja → hriSearch normalizado
            // (minúsculas, sin '#') — ver sheetNaming.normalizeHriSearch.
            const term = parsed.hri;
            const cgQuery = query(
                collectionGroup(db, 'sheets'),
                where('hriSearch', '>=', term),
                where('hriSearch', '<=', term + '\uf8ff'),
                limit(25)
            );
            const snap = await getDocs(cgQuery);
            const found = snap.docs.map(d => ({
                ...d.data(),
                id: d.id,
                jobId: d.ref.parent.parent.id,
            }));

            if (found.length > 0) {
                setSearchResults(found);
                return;
            }

            // B1b: ninguna plancha coincide → probar como CÓDIGO DE PIEZA (match exacto,
            // case-insensitive) con UNA collection-group query por `pieceCodes`. Las
            // planchas que contienen el código SON exactamente las de esa pieza; se
            // agrupan por job (un mismo código podría existir en varios jobs).
            const pieceQ = query(
                collectionGroup(db, 'sheets'),
                where('pieceCodes', 'array-contains', term),
                limit(50)
            );
            const psnap = await getDocs(pieceQ);
            if (!psnap.empty) {
                const byJob = new Map();
                psnap.docs.forEach(d => {
                    const jid = d.ref.parent.parent.id;
                    if (!byJob.has(jid)) byJob.set(jid, []);
                    byJob.get(jid).push({ id: d.id, ...d.data() });
                });
                const results = [];
                for (const [jid, sheets] of byJob.entries()) {
                    const piece = findPieceAcrossSheets(sheets, term);
                    if (piece) results.push({ jobId: jid, piece });
                }
                if (results.length === 1) {
                    const r = results[0];
                    const jobSnap = await getDoc(doc(db, 'jobs', r.jobId));
                    setSelectedPiece({ piece: r.piece, jobId: r.jobId, jobName: jobSnap.exists() ? (jobSnap.data().jobName || r.jobId) : r.jobId });
                } else {
                    setPieceResults(results);
                }
                return;
            }

            setError(`No se encontraron planchas ni piezas que coincidan con "${searchTerm}". (Las planchas/piezas creadas antes del backfill aparecen tras correr el backfill; la búsqueda de pieza requiere su código exacto.)`);

        } catch (err) {
            setError(`Error en la búsqueda: ${err.message}`);
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectSheet = async (sheet) => {
        const jobRef = doc(db, 'jobs', sheet.jobId);
        const jobSnap = await getDoc(jobRef);
        if(jobSnap.exists()) {
            setJobData({ id: jobSnap.id, ...jobSnap.data() });
            setSelectedSheet(sheet);
            setSearchResults([]);
            setSearchTerm('');
        } else {
            setError("No se pudo cargar la información del trabajo asociado.");
        }
    };

    // B1a: el marcado ya NO se escribe directo desde el cliente. Llama la Cloud
    // Function recordProductionScan, que valida contadores/permiso EN EL SERVIDOR y
    // emite el evento al bus ITC (ai_logs). Tras éxito releemos la plancha para
    // refrescar contadores. Los mensajes de error vienen del servidor (exceso de
    // repeticiones, sin permiso, etc.).
    const handleProductionUpdate = async (type) => {
        if (!selectedSheet) return;

        setIsLoading(true);
        setError('');
        setSuccess('');

        try {
            const recordProductionScan = httpsCallable(functions, 'recordProductionScan');
            await recordProductionScan({ jobId: selectedSheet.jobId, sheetId: selectedSheet.id, type });

            const sheetRef = doc(db, 'jobs', selectedSheet.jobId, 'sheets', selectedSheet.id);
            const updatedSheetSnap = await getDoc(sheetRef);
            setSelectedSheet({ id: updatedSheetSnap.id, ...updatedSheetSnap.data(), jobId: selectedSheet.jobId });

            const what = type === 'print' ? 'impresa (frente)' : type === 'printBack' ? 'impresa (dorso)' : 'cortada';
            setSuccess(`¡Éxito! 1 repetición marcada como ${what}.`);

        } catch (err) {
            setError(err.message || 'No se pudo registrar la producción.');
        } finally {
            setIsLoading(false);
        }
    };
    
    // Mismo flujo de falla que SheetDetailModal: tarea asignada a un usuario real +
    // correo (doc en `mail` → Trigger Email) + history[] de la plancha, en UN writeBatch.
    const handleReportFailure = async ({ failureType, failedCount, reason, assignedUser, aiCleanedDescription, aiSuggestedAction }) => {
        if (!selectedSheet || !jobData) return;
        const sheetName = selectedSheet.hri;
        const sheetRef = doc(db, 'jobs', jobData.id, 'sheets', selectedSheet.id);
        const batch = writeBatch(db);

        const finalDescription = aiCleanedDescription || reason;
        const isDesign = failureType === 'design';

        const baseTask = {
            jobId: jobData.id,
            sheetId: selectedSheet.id,
            relatedPlateId: selectedSheet.id,
            status: 'pending',
            createdBy: user.email,
            createdByName: user.displayName || user.email,
            createdAt: serverTimestamp(),
            originalDescription: reason,
            aiSuggestedAction: aiSuggestedAction || null,
            ...buildTaskAssignment(assignedUser),
            ...buildAssignedByStamp(user),
        };

        let taskData;
        if (isDesign) {
            batch.update(sheetRef, {
                status: 'Devuelto a Diseño',
                hasActiveAlert: true,
                history: arrayUnion({
                    action: 'Devuelto a Diseño desde Estación de Escaneo',
                    details: `Motivo: ${finalDescription}`,
                    user: user.email,
                    timestamp: new Date(),
                }),
            });
            taskData = { ...baseTask, type: 'system', title: `Corrección: Plancha ${sheetName}`, description: finalDescription };
        } else {
            // Contadores a cero AQUÍ: el ciclo de reproducción arranca al reportar la falla.
            batch.update(sheetRef, {
                ...productionResetFields(),
                hasActiveAlert: true,
                history: arrayUnion({
                    action: 'Falla de Producción Reportada',
                    details: `${failedCount} plancha(s) dañada(s). Razón: ${finalDescription}`,
                    user: user.email,
                    timestamp: new Date(),
                }),
            });
            taskData = { ...baseTask, type: 'system_reproduction', title: `Reproducción: ${failedCount}x Plancha ${sheetName}`, description: `Se solicitaron ${failedCount} planchas de reposición. Causa: ${finalDescription}` };
        }

        batch.set(doc(collection(db, 'jobs', jobData.id, 'tasks')), taskData);

        const mail = buildAssignmentEmail({
            assignedUser,
            assignedBy: user,
            taskTitle: taskData.title,
            taskDescription: taskData.description,
            jobId: jobData.id,
            suggestedAction: aiSuggestedAction,
        });
        if (mail) {
            batch.set(doc(collection(db, 'mail')), mail);
        }

        try {
            await batch.commit();
            setSuccess(`Reporte para la plancha ${sheetName} enviado y asignado.`);
            setSelectedSheet(null);
        } catch (err) {
            console.error('Error reporting failure:', err);
            setError(`No se pudo enviar el reporte: ${err.message}`);
        }
    };

    return (
        <div className="bg-gray-900 min-h-screen flex flex-col items-center p-4 text-white">
            {selectedSheet && jobData && (
                <ReportFailureModal
                    isOpen={isReportModalOpen}
                    onClose={() => setIsReportModalOpen(false)}
                    onSubmit={handleReportFailure}
                    sheetName={selectedSheet.hri}
                    users={users}
                    initialFailureType="production"
                    sheetContext={`Trabajo: ${jobData.jobName} | Reps: ${selectedSheet.repetitions || 1}`}
                />
            )}

            <div className="w-full max-w-4xl text-center">
                <Camera size={64} className="mx-auto text-indigo-400 mb-4" />
                <h1 className="text-4xl font-bold mb-2">Estación de Escaneo y Auditoría</h1>
                {/* Copy honesto: planchas (registrar producción) y piezas (consulta de estado, B1b). */}
                <p className="text-gray-400 mb-6">Busca una <b>plancha</b> por su código (o escanea su QR) para registrar producción, o una <b>pieza</b> por su código (o QR) para ver su estado.</p>

                <form onSubmit={handleSearch} className="mb-6 flex gap-2">
                    <input
                        ref={inputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Código de plancha o pieza (ej. 1116-COR_002P) o escaneo QR..."
                        className="w-full p-4 bg-gray-800 border-2 border-gray-700 rounded-lg text-lg focus:border-indigo-500 focus:outline-none"
                    />
                    <button type="submit" disabled={isLoading} className="p-4 bg-indigo-600 rounded-lg font-bold hover:bg-indigo-500 disabled:bg-gray-600">
                        <Search size={24}/>
                    </button>
                </form>

                {isLoading && <p>Buscando...</p>}
                {error && <div className="mt-4 p-4 bg-red-500/20 text-red-300 rounded-lg flex items-center justify-center gap-2"><AlertCircle size={20}/> {error}</div>}
                {success && <div className="mt-4 p-4 bg-green-500/20 text-green-300 rounded-lg flex items-center justify-center gap-2"><CheckCircle size={20}/> {success}</div>}
                
                {/* Resultados de Búsqueda */}
                {searchResults.length > 0 && (
                    <div className="mt-6 space-y-2 text-left">
                        {searchResults.map(sheet => (
                            <div key={sheet.id} onClick={() => handleSelectSheet(sheet)} className="p-3 bg-gray-800 rounded-lg flex items-center gap-4 cursor-pointer hover:bg-gray-700">
                                <img src={previewThumb(sheet) || 'https://placehold.co/100x60/374151/4f46e5?text=N/A'} alt="preview" className="w-24 h-auto rounded-md object-cover"/>
                                <div className="flex-grow">
                                    <p className="font-bold text-lg">{sheet.hri}</p>
                                    <p className="text-sm text-gray-400">{sheet.productionStatus || sheet.status}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* B1b: resultados de PIEZA por código en varios jobs (se elige uno). */}
                {pieceResults.length > 0 && (
                    <div className="mt-6 space-y-2 text-left">
                        <p className="text-sm text-gray-400 mb-1">La pieza aparece en {pieceResults.length} trabajos. Elige uno:</p>
                        {pieceResults.map(r => (
                            <div key={r.jobId} onClick={() => { setSelectedPiece({ piece: r.piece, jobId: r.jobId, jobName: r.jobId }); setPieceResults([]); }} className="p-3 bg-gray-800 rounded-lg flex items-center gap-4 cursor-pointer hover:bg-gray-700">
                                <img src={r.piece.previewUrl || 'https://placehold.co/80x80/374151/4f46e5?text=N/A'} alt="preview" className="w-16 h-16 rounded-md object-contain"/>
                                <div className="flex-grow">
                                    <p className="font-bold text-lg font-mono">{r.piece.code}</p>
                                    <p className="text-sm text-gray-400">{r.piece.name} · Trabajo {r.jobId}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* B1b: panel READ-ONLY de pieza (estado agregado a través de las planchas). */}
                {selectedPiece && (
                    <div className="mt-6 p-6 bg-gray-800 rounded-lg text-left animate-fade-in">
                        <div className="flex justify-between items-start border-b border-gray-700 pb-4 mb-4">
                            <div>
                                <h2 className="text-2xl font-bold font-mono">{selectedPiece.piece.code}</h2>
                                <p className="text-sm text-gray-400">{selectedPiece.piece.name} · Trabajo: {selectedPiece.jobName} ({selectedPiece.jobId})</p>
                            </div>
                            <button onClick={() => setSelectedPiece(null)} className="p-2 hover:bg-gray-700 rounded-full"><X size={20}/></button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="flex items-center justify-center">
                                <img src={selectedPiece.piece.previewUrl || 'https://placehold.co/300x200/374151/4f46e5?text=N/A'} alt="preview" className="max-w-full max-h-56 rounded-md object-contain border border-gray-700"/>
                            </div>
                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-2 text-center">
                                    <div className="p-3 bg-gray-700 rounded-lg"><p className="text-xs text-gray-400">Requeridas</p><p className="text-2xl font-bold">{selectedPiece.piece.totalQuantity}</p></div>
                                    <div className="p-3 bg-gray-700 rounded-lg"><p className="text-xs text-gray-400">Impresas</p><p className="text-2xl font-bold">{selectedPiece.piece.printedQuantity}</p></div>
                                    <div className="p-3 bg-gray-700 rounded-lg"><p className="text-xs text-gray-400">Cortadas</p><p className="text-2xl font-bold">{selectedPiece.piece.cutQuantity}</p></div>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-400 mb-1">Estado</p>
                                    <span className="px-3 py-1 inline-flex text-sm font-semibold rounded-full bg-gray-200 text-gray-900">{pieceProductionStatus(selectedPiece.piece).text}</span>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-400 mb-1">En planchas</p>
                                    <div className="flex flex-wrap gap-1">
                                        {Array.from(selectedPiece.piece.sheets || []).map(hri => (
                                            <span key={hri} className="bg-gray-700 px-2 py-0.5 rounded-full text-xs font-mono">{hri}</span>
                                        ))}
                                    </div>
                                </div>
                                <p className="text-xs text-gray-500">Vista de solo lectura. La producción se registra escaneando la PLANCHA.</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Panel de Acción */}
                {selectedSheet && jobData && (
                    <div className="mt-6 p-6 bg-gray-800 rounded-lg text-left animate-fade-in">
                        <div className="flex justify-between items-start border-b border-gray-700 pb-4 mb-4">
                            <div>
                                <h2 className="text-2xl font-bold">{selectedSheet.hri}</h2>
                                <p className="text-sm text-gray-400">Trabajo: {jobData.jobName} ({jobData.id})</p>
                            </div>
                            <button onClick={() => setSelectedSheet(null)} className="p-2 hover:bg-gray-700 rounded-full"><X size={20}/></button>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <h3 className="font-semibold mb-2">Preview Plancha</h3>
                                <img src={previewCard(selectedSheet) || 'https://placehold.co/400x250/374151/4f46e5?text=N/A'} alt="preview" className="w-full h-auto rounded-md object-cover border border-gray-700"/>
                                <h3 className="font-semibold mt-4 mb-2">Piezas en esta plancha</h3>
                                <div className="flex flex-wrap gap-2">
                                    {selectedSheet.pieces?.map(p => (
                                        <div key={p.code} className="p-2 bg-gray-700 rounded-md text-center">
                                            <img src={p.previewUrl || 'https://placehold.co/80x80/374151/4f46e5?text=N/A'} alt={p.code} className="w-20 h-20 object-contain rounded-md mx-auto"/>
                                            <p className="text-xs mt-1 font-mono">{p.code}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col justify-center space-y-4">
                                <h3 className="text-xl font-bold text-center">¿Qué acción deseas registrar?</h3>
                                <div className="p-4 bg-gray-700 rounded-lg text-center">
                                    <p className="text-sm text-gray-400">{isTwoSided(selectedSheet) ? 'Impresas (Frente)' : 'Impresas'}</p>
                                    <p className="text-3xl font-bold">{(selectedSheet.printedCount || 0)} / {selectedSheet.repetitions}</p>
                                </div>
                                {isTwoSided(selectedSheet) && (
                                    <div className="p-4 bg-gray-700 rounded-lg text-center">
                                        <p className="text-sm text-gray-400">Impresas (Dorso)</p>
                                        <p className="text-3xl font-bold">{printedBackOf(selectedSheet)} / {selectedSheet.repetitions}</p>
                                    </div>
                                )}
                                <div className="p-4 bg-gray-700 rounded-lg text-center">
                                    <p className="text-sm text-gray-400">Cortadas</p>
                                    <p className="text-3xl font-bold">{(selectedSheet.cutCount || 0)} / {selectedSheet.repetitions}</p>
                                </div>
                                {selectedSheet.isPrinting && (
                                    <button onClick={() => handleProductionUpdate('print')} disabled={isLoading || (selectedSheet.printedCount || 0) >= selectedSheet.repetitions} className="w-full p-4 bg-blue-600 font-bold rounded-lg flex items-center justify-center gap-2 hover:bg-blue-500 disabled:bg-gray-600">
                                        <Printer size={20}/> {isTwoSided(selectedSheet) ? 'Impresa (Frente)' : 'Marcar 1 Rep. Impresa'}
                                    </button>
                                )}
                                {isTwoSided(selectedSheet) && (
                                    <button onClick={() => handleProductionUpdate('printBack')} disabled={isLoading || printedBackOf(selectedSheet) >= selectedSheet.repetitions} className="w-full p-4 bg-indigo-600 font-bold rounded-lg flex items-center justify-center gap-2 hover:bg-indigo-500 disabled:bg-gray-600">
                                        <Printer size={20}/> Impresa (Dorso)
                                    </button>
                                )}
                                <button onClick={() => handleProductionUpdate('cut')} disabled={isLoading || (selectedSheet.cutCount || 0) >= selectedSheet.repetitions || (selectedSheet.isPrinting && (selectedSheet.cutCount || 0) >= printedDone(selectedSheet))} className="w-full p-4 bg-green-600 font-bold rounded-lg flex items-center justify-center gap-2 hover:bg-green-500 disabled:bg-gray-600">
                                    <Scissors size={20}/> Marcar 1 Rep. Cortada
                                </button>
                                <button onClick={() => setIsReportModalOpen(true)} disabled={isLoading} className="w-full p-4 bg-red-600 font-bold rounded-lg flex items-center justify-center gap-2 hover:bg-red-500 disabled:bg-gray-600">
                                    <AlertTriangle size={20}/> Reportar Falla / Faltante
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ScanningStationView;

